import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scissors, Calendar as CalIcon, Clock, X, Award, Gift, Copy, Check, Phone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/motion";
import { CANCELLATION_WINDOW_HOURS, SHOP_PHONE } from "@/lib/policies";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  confirmed: "text-[#d4af37] border-[#d4af37]/40",
  completed: "text-emerald-400 border-emerald-400/40",
  cancelled: "text-red-400 border-red-400/40",
  no_show: "text-white/40 border-white/20",
};

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isUpcoming(a) {
  if (a.status !== "confirmed") return false;
  const dt = new Date(`${a.date}T${a.time_slot}:00`);
  return dt.getTime() > Date.now();
}

function canSelfChange(a) {
  if (!isUpcoming(a)) return false;
  const dt = new Date(`${a.date}T${a.time_slot}:00`);
  return dt.getTime() - Date.now() >= CANCELLATION_WINDOW_HOURS * 3600 * 1000;
}

export default function Account() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [cancelAppt, setCancelAppt] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/appointments/me").then((r) => setAppointments(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doCancel = async () => {
    const id = cancelAppt.id;
    setCancelAppt(null);
    try {
      await api.patch(`/appointments/${id}/cancel`);
      toast.success("Appointment cancelled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-32 pb-24 px-6 lg:px-12" data-testid="account-page">
      <div className="max-w-4xl mx-auto">
        <p className="overline text-xs text-[#d4af37] mb-4">Your Account</p>
        <h1 className="font-display text-4xl lg:text-6xl text-white mb-2">Hey, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-white/50 font-body mb-12">Here&apos;s everything you&apos;ve booked with The Barbers Chair.</p>

        <LoyaltyReferral />

        <div className="flex items-center justify-between mb-8">
          <p className="overline text-xs text-white/50">Appointments ({appointments.length})</p>
          <Link to="/book" className="border border-[#d4af37] px-6 py-2.5 overline text-xs text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-colors" data-testid="account-book-btn">Book Again</Link>
        </div>

        {loading && <p className="text-white/40 font-body animate-pulse">Loading…</p>}
        {!loading && appointments.length === 0 && (
          <div className="border border-white/10 p-12 text-center bg-[#0a0a0a]">
            <Scissors className="w-8 h-8 text-[#d4af37] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-white/60 font-body mb-6">No appointments yet.</p>
            <Link to="/book" className="bg-[#d4af37] text-black px-8 py-3 overline text-xs hover:bg-[#c5a028] transition-colors">Book Your First Cut</Link>
          </div>
        )}

        <div className="space-y-4">
          {appointments.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.05} y={20}>
              <div className="border border-white/10 p-6 bg-[#0a0a0a] flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`appt-${a.id}`}>
                <div>
                  <h3 className="font-body text-lg text-white">{a.service_name}</h3>
                  <div className="flex items-center gap-6 mt-2 text-white/50 text-sm font-body">
                    <span className="flex items-center gap-2"><CalIcon className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} /> {new Date(a.date + "T00:00:00").toDateString()}</span>
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} /> {a.time_slot}</span>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <span className="font-display text-2xl text-white">${a.service_price}</span>
                  <span className={`overline text-[0.55rem] border px-3 py-1.5 ${STATUS_STYLE[a.status] || "text-white/40 border-white/20"}`}>{a.status.replace("_", " ")}</span>
                </div>
                {canSelfChange(a) && (
                  <div className="flex items-center gap-2 sm:border-l sm:border-white/10 sm:pl-5">
                    <button onClick={() => setRescheduleAppt(a)} className="border border-white/15 text-white/70 px-4 py-2 overline text-[0.6rem] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid={`reschedule-btn-${a.id}`}>Reschedule</button>
                    <button onClick={() => setCancelAppt(a)} className="border border-white/15 text-white/60 px-4 py-2 overline text-[0.6rem] hover:border-red-400 hover:text-red-400 transition-colors" data-testid={`cancel-btn-${a.id}`}>Cancel</button>
                  </div>
                )}
                {isUpcoming(a) && !canSelfChange(a) && (
                  <div className="sm:border-l sm:border-white/10 sm:pl-5 flex items-center gap-2 text-white/40 max-w-[220px]" data-testid={`within-window-note-${a.id}`}>
                    <Phone className="w-3.5 h-3.5 text-[#d4af37] shrink-0" strokeWidth={1.5} />
                    <span className="font-body text-[0.7rem] leading-tight">Within {CANCELLATION_WINDOW_HOURS}h — call {SHOP_PHONE} to change.</span>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Reschedule modal */}
      <RescheduleDialog appt={rescheduleAppt} onClose={() => setRescheduleAppt(null)} onDone={() => { setRescheduleAppt(null); load(); }} />

      {/* Cancel confirmation */}
      <AlertDialog open={!!cancelAppt} onOpenChange={(o) => !o && setCancelAppt(null)}>
        <AlertDialogContent className="bg-[#0a0a0a] border-white/15 text-white" data-testid="cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-white">Cancel this appointment?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 font-body">
              {cancelAppt && `${cancelAppt.service_name} on ${new Date(cancelAppt.date + "T00:00:00").toDateString()} at ${cancelAppt.time_slot}. This can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white" data-testid="cancel-dialog-dismiss">Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-red-500 text-white hover:bg-red-600" data-testid="cancel-dialog-confirm">Yes, cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RescheduleDialog({ appt, onClose, onDone }) {
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timeSlot, setTimeSlot] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDate(null); setSlots([]); setTimeSlot(null);
  }, [appt]);

  useEffect(() => {
    if (!date) return;
    setSlotsLoading(true);
    setTimeSlot(null);
    api.get(`/availability?date=${fmtDate(date)}`)
      .then((r) => setSlots(r.data.slots))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/appointments/${appt.id}/reschedule`, { date: fmtDate(date), time_slot: timeSlot });
      toast.success("Appointment rescheduled");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-white/15 text-white max-w-2xl" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">Reschedule {appt?.service_name}</DialogTitle>
          <DialogDescription className="text-white/50 font-body">Pick a new date and an available time for your appointment.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-6 mt-2">
          <div className="border border-white/10 p-2 bg-[#050505] flex justify-center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="reschedule-calendar"
            />
          </div>
          <div>
            <p className="overline text-xs text-[#d4af37] mb-4 flex items-center gap-2"><Clock className="w-4 h-4" /> Available Times</p>
            {!date && <p className="text-white/40 font-body text-sm">Select a date to see open slots.</p>}
            {date && slotsLoading && <p className="text-white/40 font-body text-sm animate-pulse">Loading slots…</p>}
            {date && !slotsLoading && (
              <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    disabled={!s.available}
                    onClick={() => setTimeSlot(s.time)}
                    className={`py-2.5 overline text-[0.65rem] border transition-colors duration-200 ${
                      timeSlot === s.time
                        ? "bg-[#d4af37] border-[#d4af37] text-black"
                        : s.available
                        ? "border-white/15 text-white/70 hover:border-[#d4af37] hover:text-[#d4af37]"
                        : "border-white/5 text-white/20 line-through cursor-not-allowed"
                    }`}
                    data-testid={`reschedule-slot-${s.time}`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="inline-flex items-center gap-2 border border-white/20 px-6 py-2.5 overline text-xs text-white/70 hover:text-white transition-colors" data-testid="reschedule-cancel">
            <X className="w-4 h-4" /> Close
          </button>
          <button
            onClick={save}
            disabled={!date || !timeSlot || saving}
            className="bg-[#d4af37] text-black px-8 py-2.5 overline text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c5a028] transition-colors"
            data-testid="reschedule-save"
          >
            {saving ? "Saving…" : "Confirm New Time"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoyaltyReferral() {
  const [loyalty, setLoyalty] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/loyalty/me").then((r) => setLoyalty(r.data)).catch(() => {});
    api.get("/referrals/me").then((r) => setReferral(r.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    if (!referral) return;
    navigator.clipboard.writeText(referral.link).then(() => {
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="grid md:grid-cols-2 gap-4 mb-14" data-testid="loyalty-referral">
      <div className="border border-[#d4af37]/30 bg-gradient-to-br from-[#d4af37]/[0.07] to-transparent p-7" data-testid="loyalty-card">
        <div className="flex items-center justify-between mb-5">
          <p className="overline text-xs text-[#d4af37] flex items-center gap-2"><Award className="w-4 h-4" /> Loyalty</p>
          {loyalty?.rewards_earned > 0 && (
            <span className="overline text-[0.55rem] bg-[#d4af37] text-black px-3 py-1" data-testid="loyalty-reward-badge">
              {loyalty.rewards_earned} free cut{loyalty.rewards_earned > 1 ? "s" : ""} earned
            </span>
          )}
        </div>
        {loyalty && (
          <>
            <p className="font-display text-3xl text-white mb-1">
              {loyalty.progress}<span className="text-white/30 text-2xl">/{loyalty.goal}</span>
            </p>
            <p className="text-white/50 font-body text-sm mb-5">
              {loyalty.remaining === loyalty.goal
                ? `Complete ${loyalty.goal} cuts to earn a free one.`
                : `${loyalty.remaining} more cut${loyalty.remaining > 1 ? "s" : ""} to your next free cut.`}
            </p>
            <div className="flex gap-1.5" data-testid="loyalty-progress">
              {[...Array(loyalty.goal)].map((_, i) => (
                <div key={i} className={`h-2 flex-1 ${i < loyalty.progress ? "bg-[#d4af37]" : "bg-white/10"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-7" data-testid="referral-card">
        <p className="overline text-xs text-[#d4af37] flex items-center gap-2 mb-5"><Gift className="w-4 h-4" /> Refer a Friend</p>
        <p className="text-white/50 font-body text-sm mb-5">
          Share your link — you&apos;ve referred <span className="text-white font-semibold" data-testid="referral-count">{referral?.referred_count ?? 0}</span> friend{referral?.referred_count === 1 ? "" : "s"} so far.
        </p>
        {referral && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <code className="flex-1 truncate bg-[#050505] border border-white/15 px-4 py-3 text-[#d4af37] font-body text-sm" data-testid="referral-code">{referral.code}</code>
              <button onClick={copyLink} className="border border-white/15 text-white/70 p-3 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid="copy-referral-btn">
                {copied ? <Check className="w-4 h-4 text-[#d4af37]" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-white/30 font-body text-xs">Friends who sign up with your link help you climb the chair.</p>
          </>
        )}
      </div>
    </div>
  );
}
